/**
 * Utility functions for form validation
 */

/**
 * Check password strength and return a score and message
 * @param {string} password - The password to check
 * @returns {Object} Object containing score (0-4) and descriptive message
 */
export const checkPasswordStrength = (password) => {
  // Password should be at least 8 characters
  if (!password || password.length < 8) {
    return { score: 1, message: "Password is too short" };
  }

  let score = 0;

  // Check for lowercase letters
  if (/[a-z]/.test(password)) score += 1;

  // Check for uppercase letters
  if (/[A-Z]/.test(password)) score += 1;

  // Check for numbers
  if (/[0-9]/.test(password)) score += 1;

  // Check for special characters
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  let message = "";
  if (score <= 2) {
    message = "Weak password";
  } else if (score === 3) {
    message = "Medium strength password";
  } else {
    message = "Strong password";
  }

  return { score, message };
};

/**
 * Validate email format
 * @param {string} email - The email to validate
 * @returns {boolean} True if email format is valid
 */
export const isEmailValid = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate that passwords match
 * @param {string} password - The password
 * @param {string} confirmPassword - The confirmation password
 * @returns {boolean} True if passwords match
 */
export const doPasswordsMatch = (password, confirmPassword) => {
  return password === confirmPassword;
};

/**
 * Check if password meets minimum requirements
 * @param {string} password - The password to check
 * @returns {Object} Object containing isValid flag and error message
 */
export const validatePasswordRequirements = (password) => {
  const strength = checkPasswordStrength(password);

  if (strength.score < 3) {
    return {
      isValid: false,
      message:
        "Please use a stronger password with uppercase, lowercase, numbers, and special characters",
    };
  }

  return { isValid: true, message: "" };
};
